import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function OtherPurchaseSummary() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalqty, setTotalqty] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalgst, setTotalgst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalbills, setTotalbills] = useState(0);
    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
        fetchBranchData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const handleBranchSelect = (opts) => { setSelectedBranches(opts); setDataItem([]); };

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch/${companyId}/${branchId}`, token)
            .then(r => setBranchOptions(r.response_data.map(c => ({ label: c.Name, value: c.Id }))))
            .catch(console.error);
    };

    useEffect(() => {
        let qty = 0, amt = 0, gst = 0, net = 0;
        const billNumbers = new Set();
        dataItem.forEach(item => { qty += item.Quantity; amt += item.Amount; gst += item.GSTAmount; net += item.TotalAmount; billNumbers.add(item.GRNNumber); });
        setTotalqty(qty); setTotalamt(amt); setTotalgst(gst); setTotalnet(net); setTotalbills(billNumbers.size);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);
    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]); setLoading(true);
            loadingRef.current = true;
            const branchIds = selectedBranches.length === 0 ? branchOptions.map(b => b.value) : selectedBranches.map(b => b.value);
            try {
                const response = await fetchData('OtherReportApi', `/PurchaseSummary/${companyId}/${branchIds.join(',')}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (e) { console.error(e); } finally { loadingRef.current = false; setLoading(false); }
        }
    };

    const handleSearch = (e) => setSearchQuery(e.target.value);
    const filteredDataItem = dataItem.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase())));
    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Purchase Summary');
        const rightAlignColumns = ['Quantity','Purchase Rate','GST %','Net Value','GST Amount','Net Value + Tax'];
        worksheet.mergeCells('A1:W1');
        const hc = worksheet.getCell('A1');
        hc.value = `Purchase Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        hc.font = { bold: true, size: 14 }; hc.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Branch','Purchase Type','GRN Date','GRN Number','LR Date','LR Number','PG','IG','ISG','PF','Item No','Description','HSN Code','Quantity','Net Value','GST %','GST Amount','Net Value + Tax','Comments','Category','Item Group','Season Code','Size Code'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell(cell => { cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFB6D7A8'} }; cell.font = { bold:true,color:{argb:'FF000000'} }; cell.alignment = { vertical:'middle',horizontal:'center',wrapText:true }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));
        filteredDataItem.forEach(rowData => {
            const row = worksheet.addRow([rowData.BranchName,rowData.PurchaseType,formatDate(rowData.GRNDate),rowData.GRNNumber,formatDate(rowData.LRDate),rowData.LRNumber,rowData.PG,rowData.IG,rowData.ISG,rowData.PF,parseFloat(rowData.Code),rowData.ItemName,rowData.HSNCode,parseFloat(rowData.Quantity),parseFloat(rowData.Amount.toFixed(2)),parseFloat(rowData.GSTRate),parseFloat(rowData.GSTAmount.toFixed(2)),parseFloat(rowData.TotalAmount.toFixed(2)),rowData.Remarks,rowData.CategoryName,rowData.ItemGroupName,rowData.SeasonCode,rowData.SizeCode]);
            row.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        });
        const totalRow = worksheet.addRow(['','','','','','','','','','','','Total Bills',totalbills,totalqty,totalnet,'',totalgst,totalamt,'','','','','']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFFFF2CC'} }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.views = [{ state: 'frozen', ySplit: 2 }];
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'PurchaseSummary.xlsx');
    };

    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Purchase Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label><input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} /></div>
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label><input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} /></div>
                        <div className="pg-field" style={{ minWidth: 220 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Branch <span style={{ color: 'red' }}>*</span></label>
                            <Select options={branchOptionsWithBlank} value={selectedBranches} onChange={handleBranchSelect} placeholder="Select Branch" isMulti menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }), valueContainer: b => ({ ...b, height: 31, padding: '0 6px' }), indicatorsContainer: b => ({ ...b, height: 31 }), menu: b => ({ ...b, fontSize: 12.5 }), menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}><RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}</button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && <input className="pg-input pg-search-input" placeholder="Search…" value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />}
                </div>
            </div>
            {loading && <LoadingOverlay />}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead><tr><th>Store</th><th>Purchase Type</th><th>GRN Date</th><th>GRN Number</th><th>LR Date</th><th>LR Number</th><th>PG</th><th>IG</th><th>ISG</th><th>PF</th><th>Item No</th><th>Description</th><th>HSN Code</th><th>Quantity</th><th>Net Value</th><th>GST %</th><th>GST Amount</th><th>Net Value + GST</th><th>Comments</th><th>Category</th><th>PG Category</th><th>Item Group</th><th>Season Code</th><th>Size Code</th></tr></thead>
                    <tbody>
                        {filteredDataItem.map((data, i) => (
                            <tr key={i}><td>{data.BranchName}</td><td>{data.PurchaseType}</td><td>{formatDate(data.GRNDate)}</td><td>{data.GRNNumber}</td><td>{formatDate(data.LRDate)}</td><td>{data.LRNumber}</td><td>{data.PG}</td><td>{data.IG}</td><td>{data.ISG}</td><td>{data.PF}</td><td>{data.Code}</td><td>{data.ItemName}</td><td>{data.HSNCode}</td><td style={{ textAlign: 'right' }}>{data.Quantity}</td><td style={{ textAlign: 'right' }}>{data.Amount.toFixed(2)}</td><td style={{ textAlign: 'right' }}>{data.GSTRate}</td><td style={{ textAlign: 'right' }}>{data.GSTAmount.toFixed(2)}</td><td style={{ textAlign: 'right' }}>{data.TotalAmount.toFixed(2)}</td><td>{data.Remarks}</td><td>{data.CategoryName}</td><td>{data.PGCategoryName}</td><td>{data.ItemGroupName}</td><td>{data.SeasonCode}</td><td>{data.SizeCode}</td></tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={11}></td><td>Total Bills</td><td style={{ textAlign: 'right' }}>{totalbills}</td><td style={{ textAlign: 'right' }}>{totalqty.toFixed(2)}</td><td style={{ textAlign: 'right' }}>{totalnet.toFixed(2)}</td><td></td><td style={{ textAlign: 'right' }}>{totalgst.toFixed(2)}</td><td style={{ textAlign: 'right' }}>{totalamt.toFixed(2)}</td><td colSpan={6}></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default OtherPurchaseSummary;
